import { fetchBaseQueryExtended } from "./fetchBaseQueryExtended"
import { createApi } from "@reduxjs/toolkit/query/react"

// The list of tagTypes sorted by name
const TAG_TYPES = [
  "Amenities",
  "AnalyticsDashboard",
  "Announcements",
  "AnnouncementsAdmin",
  "ApiKeys",
  "AssetAvailability",
  "AssetReservations",
  "Assets",
  "AssetSchedule",
  "AssetTypes",
  "AssetSuggestions",
  "BillingSummary",
  "Buildings",
  "Calendars",
  "CheckIns",
  "Company",
  "CreditCard",
  "CustomerInfo",
  "CustomContent",
  "Demo",
  "Departments",
  "DeskReservations",
  "Documents",
  "DeskAvailability",
  "Desks",
  "DeskSchedule",
  "DeskSuggestions",
  "EmployeeBadges",
  "Estimates",
  "Experiments",
  "FeatureFlags",
  "Floors",
  "Integrations",
  "IntegrationsApps",
  "Invites",
  "Invoices",
  "Labs",
  "Me",
  "MePermissions",
  "MeRooms",
  "Notifications",
  "Payments",
  "PeopleSchedule",
  "ProDvx",
  "Reseller",
  "RbRooms",
  "RoomReservations",
  "Rooms",
  "RoomsDevices",
  "RoomSuggestions",
  "Scim",
  "ScreeningConfigs",
  "Screenings",
  "Solutions",
  "SubscriptionPlans",
  "Subscriptions",
  "TabletSettings",
  "TeamsNotifications",
  "Timeslots",
  "Users",
  "VisitorDevices",
  "VisitorSettings",
  "Visits",
  "Zoom",
] as const

// Define a service using a base URL and expected endpoints
export const api = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQueryExtended,
  tagTypes: TAG_TYPES,
  endpoints: () => ({}),
})

export type TagType = (typeof TAG_TYPES)[number]

/**
 * Returns an `invalidatesTags` callback that invalidates `success` tags when the
 * mutation has no error, otherwise `error` tags.
 * Uses the error argument (not result truthiness) so empty DELETE/204 bodies still
 * invalidate on success. In most cases you only need the success array.
 *
 * @param success - Tags to invalidate when the mutation succeeds.
 * @param error - Tags to invalidate when the mutation fails.
 * @example
 * invalidatesTags: invalidateOn({
 *   success: ['Company', 'Assets'],
 *   error: ['Buildings']
 * });
 *
 */
export const invalidateOn =
  ({ success = [], error = [] }: { success?: TagType[]; error?: TagType[] }) =>
  (_result: unknown, mutationError: unknown) =>
    mutationError ? error : success

export const DESK_SCHEDULE_TAGS = [
  "DeskAvailability",
  "DeskSchedule",
] as const satisfies readonly TagType[]

// People schedule rows carry the desk name and reservations, which editing or deleting a desk changes
export const DESK_CHANGE_SCHEDULE_TAGS = [
  ...DESK_SCHEDULE_TAGS,
  "PeopleSchedule",
] as const satisfies readonly TagType[]

export const ASSET_SCHEDULE_TAGS = [
  "AssetAvailability",
  "AssetSchedule",
] as const satisfies readonly TagType[]
